import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Billing } from '../services/api';
import { PLAN_LABELS, PLAN_PRICE_LABEL, PLAN_LIMITS } from '../config/plans';

const FEATURES = [
  ['Users included',            p => PLAN_LIMITS[p].users === Infinity ? 'Unlimited' : PLAN_LIMITS[p].users],
  ['Quotes per month',          p => PLAN_LIMITS[p].quotesPerMonth === Infinity ? 'Unlimited' : PLAN_LIMITS[p].quotesPerMonth],
  ['Quote approval workflow',   p => p === 'free' ? 'Single-step' : 'Manager → Finance'],
  ['Visits & Expenses (GPS)',   p => p !== 'free' ? '✓' : '—'],
  ['Follow-ups tracker',        p => p !== 'free' ? '✓' : '—'],
  ['Reports & analytics',       p => p !== 'free' ? '✓' : '—'],
  ['OCR card scans / month',    p => PLAN_LIMITS[p].ocrScansPerMonth === Infinity ? 'Unlimited' : PLAN_LIMITS[p].ocrScansPerMonth],
  ['Custom branding on PDFs',   p => p !== 'free' ? '✓' : '—'],
  ['Multiple branches',         p => p === 'enterprise' ? '✓' : '—'],
  ['Report exports (CSV/PDF)',  p => p === 'enterprise' ? '✓' : '—'],
  ['White-label PDFs',          p => p === 'enterprise' ? '✓' : '—'],
  ['Support',                   p => p === 'free' ? 'Community' : p === 'growth' ? 'Priority email' : 'Dedicated + SLA'],
];

// Loads Razorpay's checkout.js only once, on demand
function loadRazorpayScript() {
  return new Promise((resolve, reject) => {
    if (window.Razorpay) { resolve(); return; }
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = resolve;
    script.onerror = () => reject(new Error('Could not load payment gateway'));
    document.body.appendChild(script);
  });
}

export default function UpgradePlan() {
  const { user } = useAuth();
  const currentPlan = user?.plan || 'free';
  const [payingPlan, setPayingPlan] = useState(null);
  const [err, setErr] = useState('');

  const startCheckout = async (plan) => {
    setErr('');
    setPayingPlan(plan);
    try {
      await loadRazorpayScript();
      const order = await Billing.createOrder(plan);
      if (order.error) throw new Error(order.error);

      const rzp = new window.Razorpay({
        key: order.key_id,
        amount: order.amount,
        currency: order.currency,
        order_id: order.order_id,
        name: 'BizFlowSuite',
        description: `Upgrade to ${PLAN_LABELS[plan]} plan`,
        prefill: { name: user?.name, email: user?.email },
        theme: { color: '#1d4ed8' },
        handler: async (response) => {
          try {
            const result = await Billing.verifyPayment({
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });
            if (result.error) throw new Error(result.error);

            // Plan is now upgraded server-side. Simplest reliable way to
            // pick up the new plan client-side is a full reload — it
            // forces AuthContext/session state to be re-derived fresh.
            // If your AuthContext exposes a lighter "refreshSession()"
            // method, swap this for that instead of a full reload.
            window.location.reload();
          } catch (ex) {
            setErr(`Payment succeeded but activation failed: ${ex.message}. Contact support with payment ID ${response.razorpay_payment_id}.`);
          }
        },
        modal: {
          ondismiss: () => setPayingPlan(null),
        },
      });

      rzp.on('payment.failed', (resp) => {
        setErr(`Payment failed: ${resp.error.description}`);
        setPayingPlan(null);
      });

      rzp.open();
    } catch (ex) {
      setErr(ex.message);
      setPayingPlan(null);
    }
  };

  return (
    <div className="page">
      <div className="page-header">
        <div className="page-title">Plans & Pricing</div>
      </div>

      {err && <div className="alert alert-error" style={{ marginBottom: '1rem' }}>{err}</div>}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '1.2rem', marginBottom: '2rem' }}>
        {['free', 'growth', 'enterprise'].map(plan => (
          <div key={plan} className="card" style={{
            border: plan === currentPlan ? '2px solid var(--accent)' : '1px solid var(--border)',
            position: 'relative',
          }}>
            {plan === currentPlan && (
              <span className="badge" style={{ position: 'absolute', top: 12, right: 12, background: 'var(--accent)', color: '#fff', fontSize: '.7rem' }}>
                Current
              </span>
            )}
            <div style={{ fontWeight: 700, fontSize: '1.2rem', marginBottom: '.3rem' }}>{PLAN_LABELS[plan]}</div>
            <div style={{ color: 'var(--muted)', fontSize: '.9rem', marginBottom: '1.2rem' }}>{PLAN_PRICE_LABEL[plan]}</div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '.5rem', marginBottom: '1.4rem' }}>
              {FEATURES.map(([label, fn]) => (
                <div key={label} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '.82rem' }}>
                  <span style={{ color: 'var(--muted)' }}>{label}</span>
                  <span style={{ fontWeight: 600 }}>{fn(plan)}</span>
                </div>
              ))}
            </div>

            {plan === currentPlan ? (
              <button className="btn btn-ghost" disabled style={{ width: '100%' }}>Your Current Plan</button>
            ) : plan === 'enterprise' ? (
              <a className="btn btn-primary" style={{ width: '100%', textAlign: 'center', display: 'block' }}
                href="mailto:sales@bizflowsuite.com?subject=Enterprise%20Plan%20Inquiry">
                Contact Sales
              </a>
            ) : (
              <button className="btn btn-primary" style={{ width: '100%' }}
                onClick={() => startCheckout(plan)} disabled={payingPlan === plan}>
                {payingPlan === plan ? 'Opening checkout…' : `Upgrade to ${PLAN_LABELS[plan]}`}
              </button>
            )}
          </div>
        ))}
      </div>

      <p style={{ color: 'var(--faint)', fontSize: '.78rem' }}>
        Payments are processed securely by Razorpay. Your plan activates automatically once payment is confirmed.
      </p>
    </div>
  );
}